import './ChangePhoneNumber.scss';

import { ButtonV2, PhoneInput, Spinner, Toast } from '@travelwin/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../components/Modal';
import SuccessMessage from '../../components/SuccessMessage';
import {
  CODE_LENGTH,
  PHONE_LENGTH_MAX,
  PHONE_LENGTH_MIN,
  usePhoneNumberVerification,
} from '../../hooks/usePhoneNumberVerification';

const className = 'c-ChangePhoneNumber';

interface Props {
  open: boolean;
  handleClose: (phoneNumber?: string) => void;
  addNew: boolean;
}

interface ViewStateConfig {
  title: string | undefined;
  hideCloseButton: boolean;
}

const ChangePhoneNumber = ({ open, handleClose, addNew }: Props) => {
  const { t } = useTranslation();
  const {
    isLoading,
    viewState,
    errorMessage,
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    handleSendPhoneNumber,
    handleVerificationCode,
    handleResendVerificationCode,
  } = usePhoneNumberVerification({
    validatePhoneNumber: () => Promise.resolve(true),
  });

  const showSpinner = () => isLoading && <Spinner size={12} color="white" />;

  const viewStateConfig: ViewStateConfig = useMemo(() => {
    switch (viewState) {
      case 'ENTER_PHONE_NUMBER':
        return {
          title: addNew
            ? t('change_phone_number.add_new.title', 'Add Phone Number')
            : t(
                'change_phone_number.change_current.title',
                'Change Phone Number',
              ),
          hideCloseButton: false,
        };
      case 'ENTER_VERIFICATION_CODE':
        return {
          title: t(
            'change_phone_number.verification_code.title',
            'Verification Code',
          ),
          hideCloseButton: false,
        };
      case 'PHONE_NUMBER_VALIDATED':
        return {
          title: undefined,
          hideCloseButton: true,
        };
      default: {
        return {
          title: undefined,
          hideCloseButton: false,
        };
      }
    }
  }, [viewState, addNew]);

  const handleDone = () => {
    handleClose(phoneNumber);
  };

  const renderView = () => {
    switch (viewState) {
      case 'ENTER_PHONE_NUMBER':
        return (
          <div className={`${className}__content`}>
            <p className={`${className}__description`}>
              {addNew
                ? t(
                    'change_phone_number.add_new.description',
                    'To add your mobile number, please enter the new mobile number.',
                  )
                : t(
                    'change_phone_number.change_current.description',
                    'To change your mobile number, please enter the new mobile number.',
                  )}
            </p>
            <div className={`${className}__phone-number`}>
              <PhoneInput
                onPhoneNumberChange={(phoneNumber) =>
                  setPhoneNumber(phoneNumber)
                }
              />
            </div>
            {errorMessage && <Toast error>{errorMessage}</Toast>}
            <div className={`${className}__button-container`}>
              <ButtonV2
                fluid
                variant="primary"
                size="large"
                disabled={
                  phoneNumber.length < PHONE_LENGTH_MIN ||
                  phoneNumber.length > PHONE_LENGTH_MAX ||
                  isLoading
                }
                onClick={() => handleSendPhoneNumber()}
                data-testid="button-send-mobile-number"
              >
                {t('change_phone_number.send', 'Send')} {showSpinner()}
              </ButtonV2>
            </div>
          </div>
        );
      case 'ENTER_VERIFICATION_CODE':
        return (
          <div className={`${className}__content`}>
            <p className={`${className}__description`}>
              {t(
                'change_phone_number.verification_code.description',
                'We have sent an SMS code with a verification code to {{phoneNumber}}. To complete the verification process please enter the 6-digit code below.',
                { phoneNumber },
              )}
            </p>
            <input
              data-testid="input-verification-code"
              value={verificationCode}
              onChange={(e) => {
                if (e.target.value.length <= CODE_LENGTH)
                  setVerificationCode(e.target.value);
              }}
              placeholder={t(
                'change_phone_number.verification_code.placeholder',
                'Enter Verification Code',
              )}
              className={`${className}__input`}
            />
            {errorMessage && <Toast error>{errorMessage}</Toast>}
            <p className={`${className}__resend-message`}>
              {t(
                'change_phone_number.verification_code.resend_message',
                'If you didn’t receive a code click the link below to resend the SMS code.',
              )}
            </p>
            <ButtonV2
              fluid
              variant="primary"
              size="large"
              disabled={verificationCode.length !== CODE_LENGTH || isLoading}
              onClick={() => handleVerificationCode()}
              data-testid="button-verify-code"
            >
              {t('free_trial.verification_code.verify', 'Verify')}{' '}
              {showSpinner()}
            </ButtonV2>
            <ButtonV2
              fluid
              variant="secondary"
              size="large"
              disabled={isLoading}
              onClick={() => handleResendVerificationCode()}
              data-testid="button-resend-code"
            >
              {t('free_trial.verification_code.resend', 'Resend Code')}{' '}
              {showSpinner()}
            </ButtonV2>
          </div>
        );
      case 'PHONE_NUMBER_VALIDATED':
        return (
          <SuccessMessage
            title={t(
              'change_phone_number.phone_number_valid.title',
              'Congratulations',
            )}
            description={t(
              'change_phone_number.phone_number_valid.description',
              'Your mobile number has been verified successfully.',
            )}
            buttonText={t(
              'change_phone_number.phone_number_valid.button',
              'Continue',
            )}
            buttonDataTestId="phone-number-verified-button"
            onButtonClick={handleDone}
          />
        );
    }
  };

  return (
    <Modal
      toggle={handleClose}
      on={open}
      title={viewStateConfig.title}
      hideCloseButton={viewStateConfig.hideCloseButton}
      fullScreen
    >
      <div className={className}>{renderView()}</div>
    </Modal>
  );
};

export default ChangePhoneNumber;
